import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared categories fetcher — de-duplicates network calls across all
 * components that render at the same time (Header, HomePage, ProductsPage
 * sidebar, mobile menu). Keeps a module-scoped in-memory cache + a single
 * in-flight promise so N components = 1 XHR.
 *
 * Cache is invalidated by `bumpCategoriesCache()` (call after admin edits).
 */
let cache = null;
let inflight = null;
const subscribers = new Set();

async function fetchCategoriesOnce() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = axios.get(`${API}/categories`)
    .then((r) => {
      cache = Array.isArray(r.data) ? r.data : [];
      inflight = null;
      subscribers.forEach((cb) => cb(cache));
      return cache;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function bumpCategoriesCache() {
  cache = null;
  inflight = null;
  // Force any live subscribers to refetch.
  fetchCategoriesOnce().catch(() => {});
}

export function useCategoriesData() {
  const [categories, setCategories] = useState(cache || []);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let mounted = true;
    if (cache) {
      setCategories(cache);
      setLoading(false);
    } else {
      fetchCategoriesOnce()
        .then((data) => { if (mounted) { setCategories(data); setLoading(false); } })
        .catch(() => { if (mounted) setLoading(false); });
    }
    // Live re-render if another caller refreshes the cache
    const onUpdate = (next) => { if (mounted) setCategories(next); };
    subscribers.add(onUpdate);
    return () => {
      mounted = false;
      subscribers.delete(onUpdate);
    };
  }, []);

  return { categories, loading };
}
